import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  Users,
  CreditCard,
  Utensils,
  Dumbbell,
  Building2,
  UserCog,
  Star,
  Tag,
  MessageSquare,
  QrCode,
  X,
  LayoutDashboard,
  Calendar,
  CalendarDays,
  UtensilsCrossed,
  CalendarCheck,
  BookOpen,
  ChevronRight,
  ChevronDown,
  Receipt,
  ShoppingBag,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useMobile } from '@/contexts/mobile-context';
import { useUser } from '@/contexts/user-context';
import { useGym } from '@/contexts/gym-context';
import { useView } from '@/contexts/view-context';
import { TenantLogo, TenantLogoSmall } from '@/components/shared/tenant-logo';
import { useChatUnreadCount } from '@/api/chat';

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
}

export function Sidebar({ className, isOpen, ...props }: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile, isSidebarOpen, closeSidebar } = useMobile();
  const { user } = useUser();
  const { gyms } = useGym();
  const { viewMode } = useView();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(['Diet Management', t('sidebar.classes', 'Classes')]));
  const isOwner = user?.userType?.toLowerCase() === 'owner';
  const { data: unreadData } = useChatUnreadCount();

  // Check if user has gyms
  const hasGyms = gyms && gyms.length > 0;

  console.log('Sidebar: hasGyms', hasGyms, 'gyms', gyms);

  const handleNavigation = (href: string) => {
    if (isMobile) {
      closeSidebar();
      setTimeout(() => {
        navigate(href);
      }, 100);
    } else {
      navigate(href);
    }
  };

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  };

  const isGroupActive = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some((child) => child.href === pathname);
  };

  // Define navigation item type
  type NavItem = {
    title: string;
    href?: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: string;
    children?: NavItem[];
  };

  // Define base navigation items
  const baseNavItems: NavItem[] = [
    {
      title: t('sidebar.dashboard'),
      href: '/dashboard',
      icon: LayoutDashboard,
    },
  ];

  // Define admin navigation items
  const adminNavItems = [
    ...baseNavItems,
    // Only show these items if user has gyms
    ...(hasGyms
      ? [
          {
            title: t('sidebar.users'),
            href: '/users',
            icon: Users,
          },
          {
            title: t('sidebar.plans'),
            href: '/plans',
            icon: CreditCard,
          },
          {
            title: t('sidebar.payments'),
            href: '/payments',
            icon: CreditCard,
          },
          {
            title: t('sidebar.checkIn'),
            href: '/check-in',
            icon: QrCode,
          },
          {
            title: t('sidebar.gyms'),
            href: '/gyms',
            icon: Building2,
          },
          {
            title: t('sidebar.staff'),
            href: '/staff',
            icon: UserCog,
          },
          {
            title: t('sidebar.classes', 'Classes'),
            icon: Calendar,
            children: [
              {
                title: t('sidebar.manageClasses', 'Manage Classes'),
                href: '/classes',
                icon: Calendar,
              },
              {
                title: t('sidebar.classBookings', 'Bookings'),
                href: '/class-bookings',
                icon: CalendarDays,
              },
            ],
          },
          /* {
            title: t("financialReports.title"),
            href: "/financial-reports",
            icon: LineChart,
          },*/
          {
            title: 'Diet Management',
            icon: Utensils,
            children: [
              {
                title: 'Diet Plans',
                href: '/diet-plans-v2',
                icon: UtensilsCrossed,
              },
              {
                title: 'Food Library',
                href: '/food-library',
                icon: BookOpen,
              },
              {
                title: 'Diet Calendar',
                href: '/diet-calendar',
                icon: CalendarCheck,
              },
            ],
          },
          {
            title: t('sidebar.exercisePlans'),
            href: '/exercise-plans',
            icon: Dumbbell,
          },
          {
            title: t('sidebar.reviews'),
            href: '/reviews',
            icon: Star,
          },
          {
            title: 'Promotions',
            href: '/promotions',
            icon: Tag,
          },
          {
            title: 'Store',
            href: '/store',
            icon: ShoppingBag,
          },
          ...(isOwner
            ? [
                {
                  title: t('sidebar.chat'),
                  href: '/chat',
                  icon: MessageSquare,
                  badge: unreadData?.unreadCount ? String(unreadData.unreadCount) : undefined,
                },
              ]
            : []),
        ]
      : []),
    // Billing is always visible for admin/owner
    {
      title: t('sidebar.billing'),
      href: '/billing',
      icon: Receipt,
    },
  ];

  // Define reception navigation items
  const receptionNavItems = [
    ...baseNavItems,
    {
      title: t('sidebar.users'),
      href: '/users',
      icon: Users,
    },
    {
      title: t('sidebar.payments'),
      href: '/payments',
      icon: CreditCard,
    },
    {
      title: t('sidebar.exercisePlans'),
      href: '/exercise-plans',
      icon: Dumbbell,
    },
    {
      title: 'Diet Management',
      icon: Utensils,
      children: [
        {
          title: 'Diet Plans',
          href: '/diet-plans-v2',
          icon: UtensilsCrossed,
        },
        {
          title: 'Food Library',
          href: '/food-library',
          icon: BookOpen,
        },
      ],
    },
    // Check-in removed as it's now part of the dashboard
  ];

  // Define trainer navigation items
  const trainerNavItems = [
    ...baseNavItems,
    {
      title: t('sidebar.appointments'),
      href: '/appointments',
      icon: Calendar,
    },
    {
      title: t('sidebar.exercisePlans'),
      href: '/exercise-plans',
      icon: Dumbbell,
    },
    {
      title: 'Diet Management',
      icon: Utensils,
      children: [
        {
          title: 'Diet Plans',
          href: '/diet-plans-v2',
          icon: UtensilsCrossed,
        },
      ],
    },
  ];

  // Get the appropriate nav items based on the user's role
  let navItems = baseNavItems;

  if (viewMode === 'admin' || user?.userType?.toLowerCase() === 'owner') {
    navItems = adminNavItems;
  } else if (viewMode === 'reception') {
    navItems = receptionNavItems;
  } else if (viewMode === 'trainer') {
    navItems = trainerNavItems;
  }

  const renderNavItem = (item: NavItem, index: number) => {
    // If item has children, render as a collapsible group
    if (item.children) {
      const isOpen = openGroups.has(item.title);
      const isActive = isGroupActive(item);

      return (
        <Collapsible key={`${item.title}-${index}`} open={isOpen} onOpenChange={() => toggleGroup(item.title)}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'w-full justify-start',
                isActive && 'bg-accent text-accent-foreground'
              )}
            >
              {React.createElement(item.icon, { className: 'mr-2 h-4 w-4' })}
              <span className="flex-1 text-left">{item.title}</span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 ml-auto" />
              ) : (
                <ChevronRight className="h-4 w-4 ml-auto" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4">
            {item.children.map((child, childIndex) => (
              <Button
                key={`${child.href}-${childIndex}`}
                variant="ghost"
                className={cn(
                  'w-full justify-start',
                  pathname === child.href
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onClick={() => child.href && handleNavigation(child.href)}
              >
                {React.createElement(child.icon, { className: 'mr-2 h-4 w-4' })}
                <span className="flex-1 text-left">{child.title}</span>
                {child.badge && (
                  <Badge variant="secondary" className="ml-auto text-xs px-1.5 py-0">
                    {child.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    // Regular item without children
    return (
      <Button
        key={`${item.href}-${index}`}
        variant="ghost"
        className={cn(
          'justify-start',
          pathname === item.href
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
        onClick={() => item.href && handleNavigation(item.href)}
      >
        {React.createElement(item.icon, { className: 'mr-2 h-4 w-4' })}
        <span className="flex-1 text-left">{item.title}</span>
        {item.badge && (
          <Badge variant="secondary" className="ml-auto text-xs px-1.5 py-0">
            {item.badge}
          </Badge>
        )}
      </Button>
    );
  };

  if (isMobile) {
    return (
      <Sheet open={isSidebarOpen} onOpenChange={closeSidebar}>
        <SheetContent side="left" className="p-0 w-[240px]">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between h-14 px-4 border-b">
              <TenantLogoSmall />
              {/* <Button variant="ghost" size="icon" onClick={closeSidebar}>
                <X className="h-4 w-4" />
              </Button> */}
            </div>
            <ScrollArea className="flex-1">
              <nav className="grid gap-1 p-2">
                {navItems.map((item, index) => renderNavItem(item, index))}
              </nav>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className={cn('h-full border-r bg-background', className)} {...props}>
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center border-b px-4">
          <TenantLogo />
        </div>
        <ScrollArea className="flex-1">
          <nav className="grid gap-1 p-2">
            {navItems.map((item, index) => renderNavItem(item, index))}
          </nav>
        </ScrollArea>
      </div>
    </div>
  );
}
